<template>
    <div>
        <!-- Live action buttons + Enqueue UI row. Bottom-aligned so the enqueue button stays on the same line
             as the three download buttons, however tall the checkbox stack above it grows. -->
        <div class="kgr-row kgr-postfix kgr-postfix--between kgr-postfix--bottom">
            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--bottom kgr-row">
                <div class="kgr-postfix__fixed">
                    <input
                        type="button"
                        :value="$t('Liste anzeigen')"
                        class="kgr-button kgr-button--accent"
                        @click="viewList"
                    />
                </div>
                <div class="kgr-postfix__fixed">
                    <input
                        type="button"
                        :value="$t('Excel-Download')"
                        class="kgr-button kgr-button--accent"
                        @click="downloadExcel"
                    />
                </div>
                <div class="kgr-postfix__fixed">
                    <input
                        type="button"
                        :value="$t('PDF-Download')"
                        class="kgr-button kgr-button--accent"
                        @click="downloadPdf"
                    />
                </div>
            </div>

            <!-- Enqueue UI. Carries kgr-row like the button group opposite it: the bottom alignment lines up
                 margin boxes, so without the same row margin this column would hang lower by exactly that. -->
            <div class="kgr-postfix kgr-row">
                <div
                    v-if="!inProgress"
                    class="animated fadeIn"
                >
                    <!-- Only the PDF is offered as a choice: it is ~80% of a long run's time, while HTML and
                         Excel are cheap enough to always build. Kindergartens keep building all three and are
                         not shown a choice they do not have. -->
                    <div class="enqueue-options">
                        <kgr-checkbox
                            v-if="formatSelectable"
                            v-model="wantsPdf"
                            :label="$t('PDF erstellen (verlängert die Berechnung deutlich)')"
                        />
                        <kgr-checkbox
                            v-model="notifyUser"
                            :label="$t('per E-Mail benachrichtigen')"
                        />
                    </div>
                    <div class="kgr-postfix__fixed">
                        <kgr-button @click="enqueue()">
                            <span v-if="averageDuration">
                                {{ $t('Liste im Hintergrund vorberechnen (ca. {duration})', { duration: averageDurationFormatted }) }}
                            </span>
                            <span
                                v-else
                                v-t="'Liste im Hintergrund vorberechnen'"
                            ></span>
                        </kgr-button>
                    </div>
                </div>
                <div
                    v-else
                    class="animated fadeIn"
                >
                    <div class="kgr-text">
                        {{ $t('Liste wird im Hintergrund vorberechnet...') }}
                    </div>
                    <div class="kgr-text">
                        {{ durationLeftFormatted() }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Prefetched data alert -->
        <div
            v-if="showPrefetchedDataBlock"
            class="kgr-row animated fadeIn"
        >
            <div class="kgr-alert kgr-alert--warning">
                <div class="kgr-row">
                    {{ $t('Liste mit vorberechneten Daten verfügbar, Stand') }}
                    <span>{{ prefetchedDataFormatted }}</span>:
                </div>
                <div class="kgr-postfix kgr-postfix--gutters">
                    <!-- A format the run did not build has no file, and its download route answers 404 by
                         design. So the button is disabled rather than left to fail. -->
                    <div class="kgr-postfix__fixed">
                        <input
                            type="button"
                            :value="$t('Liste anzeigen')"
                            class="kgr-button kgr-button--accent"
                            :disabled="!hasFormat('html')"
                            :title="hasFormat('html') ? '' : $t('Für diese Berechnung nicht erzeugt')"
                            @click="viewPrefetchedData"
                        />
                    </div>
                    <div class="kgr-postfix__fixed">
                        <input
                            type="button"
                            :value="$t('Excel-Download')"
                            class="kgr-button kgr-button--accent"
                            :disabled="!hasFormat('xlsx')"
                            :title="hasFormat('xlsx') ? '' : $t('Für diese Berechnung nicht erzeugt')"
                            @click="downloadPrefetchedExcel"
                        />
                    </div>
                    <div class="kgr-postfix__fixed">
                        <input
                            type="button"
                            :value="$t('PDF-Download')"
                            class="kgr-button kgr-button--accent"
                            :disabled="!hasFormat('pdf')"
                            :title="hasFormat('pdf') ? '' : $t('Für diese Berechnung nicht erzeugt')"
                            @click="downloadPrefetchedPdf"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Checkbox from '../common/form/checkbox/Checkbox';
import EventBus from '../../js/service/EventBus';
import KgrButton from '../common/form/button/KgrButton';
import ListPrefetchedDataRepository from '../../js/repository/ListPrefetchedDataRepository';
import moment from 'moment';

let activeController = null;

export default {
    components: {
        'kgr-checkbox': Checkbox,
        KgrButton,
    },
    props: {
        listId: {
            type: Number,
            required: false,
        },
        institutionType: {
            type: String,
            required: true,
            validator: (value) => ['federation', 'kindergarten', 'club'].includes(value),
        },
        institutionId: {
            type: Number,
            required: false,
        },
    },
    data() {
        return {
            enqueued: null,
            prefetchedData: null,
            prefetchedDataOutdated: false,
            enqueuing: false,
            enqueuingOverall: false,
            averageDuration: null,
            checkingIfEnqueued: false,
            notifyUser: false,
            originalConfigurationHash: null,
            availableFormats: [],
            formatSelectable: false,
            // The formats built regardless of the checkbox. Overwritten from the enqueue GET, so the server
            // stays the single source of truth; this value only covers the request not having answered yet.
            defaultFormats: ['html', 'xlsx'],
            // Off by default: the PDF is a small share of downloads and the large majority of the run time.
            wantsPdf: false,
        };
    },
    computed: {
        inProgress() {
            return this.enqueuing || this.enqueuingOverall || !!this.enqueued;
        },
        averageDurationFormatted() {
            return moment.duration(this.averageDuration, 'seconds').humanize();
        },
        showPrefetchedDataBlock() {
            return this.prefetchedData && !this.prefetchedDataOutdated;
        },
        selectedFormats() {
            // The PDF is the only opt-in; everything else the server calls default is built either way.
            const formats = this.defaultFormats.filter((format) => format !== 'pdf');
            return this.wantsPdf ? [...formats, 'pdf'] : formats;
        },
        prefetchedDataFormatted() {
            if (!this.prefetchedData) {
                return '';
            }
            return moment(this.prefetchedData).format('DD.MM.YYYY HH:mm');
        },
        liveRoutes() {
            const routes = {
                federation: {
                    view: '/backend/lists/federationview/',
                    pdf: '/backend/lists/federation/pdf',
                    excel: '/backend/lists/federation/excel',
                },
                kindergarten: {
                    view: '/backend/lists/kindergartenview/',
                    pdf: '/backend/lists/kindergarten/pdf',
                    excel: '/backend/lists/kindergarten/excel',
                },
                club: {
                    view: `/backend/lists/clubview/${this.institutionId}`,
                    pdf: `/backend/lists/clubs/${this.institutionId}/pdf`,
                    excel: `/backend/lists/clubs/${this.institutionId}/excel`,
                },
            };
            return routes[this.institutionType] || {};
        },
        savedListRoutes() {
            if (!this.listId) {
                return null;
            }

            const routes = {
                federation: {
                    view: `/backend/lists/federation/${this.listId}/view`,
                    pdf: `/backend/lists/federation/${this.listId}/pdf`,
                    excel: `/backend/lists/federation/${this.listId}/excel`,
                },
                kindergarten: {
                    view: `/backend/lists/kindergarten/${this.listId}/view`,
                    pdf: `/backend/lists/kindergarten/${this.listId}/pdf`,
                    excel: `/backend/lists/kindergarten/${this.listId}/excel`,
                },
                club: {
                    view: `/backend/lists/clubs/${this.institutionId}/${this.listId}/view`,
                    pdf: `/backend/lists/clubs/${this.institutionId}/${this.listId}/pdf`,
                    excel: `/backend/lists/clubs/${this.institutionId}/${this.listId}/excel`,
                },
            };
            return routes[this.institutionType] || null;
        },
    },

    watch: {
        listId: {
            handler(id) {
                if (!id) {
                    return;
                }
                this.updateConfigurationHash();
                this.checkIfEnqueued();
            },
            immediate: true,
        },
    },
    mounted() {
        EventBus.$on('list:configuration-saved', this.updateConfigurationHash);
        setInterval(this.checkIfEnqueued, 3000);
        window?.initializeListConfigurator?.();

        setTimeout(() => {
            window?.initializeChosen?.();
        }, 500);
    },
    beforeUnmount() {
        EventBus.$off('list:configuration-saved');
    },

    methods: {
        durationLeftFormatted() {
            if (!this.enqueued || !this.averageDuration) {
                return null;
            }

            const timePast = moment().diff(this.enqueued, 'seconds');
            if (timePast >= this.averageDuration) {
                return null;
            }

            const timeLeft = moment.duration(this.averageDuration - timePast, 'seconds').humanize();

            return this.$t('Restdauer: circa {timeLeft}', { timeLeft });
        },
        checkIfEnqueued() {
            if (this.checkingIfEnqueued || !this.listId) {
                return;
            }

            this.checkingIfEnqueued = true;
            ListPrefetchedDataRepository.checkIfEnqueued(this.listId)
                .then((data) => {
                    if (!this.enqueuing) {
                        this.enqueuingOverall = false;
                    }

                    this.checkingIfEnqueued = false;
                    this.enqueued = data.enqueued;
                    this.prefetchedData = data.prefetchedData;
                    this.prefetchedDataOutdated = data.outdated;
                    this.averageDuration = data.averageDuration;
                    this.availableFormats = data.availableFormats || [];
                    this.formatSelectable = !!data.formatSelectable;
                    if (data.defaultFormats && data.defaultFormats.length > 0) {
                        this.defaultFormats = data.defaultFormats;
                    }
                })
                .catch(() => this.$notify.error('Fehler beim Überprüfen der Berechnung im Hintergrund'));
        },
        hasFormat(format) {
            // Empty means either no run exists yet (the whole block is hidden then) or a backend that does not
            // send the field. Both fall back to enabling everything, i.e. the behaviour before this existed.
            // Runs that predate format recording are NOT this case: the server resolves those to all three.
            return this.availableFormats.length === 0 || this.availableFormats.includes(format);
        },
        getPrefetchedDataUrl(suffix = '') {
            return `/backend/lists/${this.listId}/prefetchData${suffix}`;
        },
        viewPrefetchedData() {
            this.loadPrefetchedDataView();
        },
        async loadPrefetchedDataView() {
            const listEl = document.getElementById('list');
            if (!listEl) {
                return;
            }

            try {
                const response = await fetch(this.getPrefetchedDataUrl());
                const html = await response.text();
                listEl.innerHTML = html;
                window.showListActions?.();
            } catch {
                this.$notify.error('Fehler beim Laden der Liste');
            }
        },
        downloadPrefetchedExcel() {
            window.location.href = this.getPrefetchedDataUrl('/excel');
        },
        downloadPrefetchedPdf() {
            window.location.href = this.getPrefetchedDataUrl('/pdf');
        },

        // Live list actions (current configuration)
        async viewList() {
            if (!this.isInEditMode() && !this.hasUnsavedChanges()) {
                return this.viewSavedList();
            }

            return this.viewListPreview();
        },
        async viewListPreview() {
            const listEl = document.getElementById('list');
            if (!listEl || !this.liveRoutes.view) {
                return;
            }

            const formData = this.serializeListConfiguration();
            if (!formData) {
                return;
            }

            document.getElementById('tooltip_listLiveView')?.style.setProperty('display', 'none');
            window.showSpinner?.();

            const controller = new AbortController();
            activeController = controller;
            window.abortCurrentListRequest = () => controller.abort();

            try {
                const response = await fetch(this.liveRoutes.view, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: formData,
                    signal: controller.signal,
                });

                if (!response.ok) {
                    // Without this, a server error (e.g. a list too large to render, which 500s) was silently
                    // injected as its error body and never surfaced to the user - the spinner just stopped with
                    // no message. Mirrors viewSavedList().
                    throw new Error(`HTTP ${response.status}`);
                }

                const html = await response.text();
                listEl.innerHTML = html;
                window.showListActions?.();
            } catch (e) {
                if (e?.name !== 'AbortError') {
                    this.$notify.error('Fehler beim Laden der Liste');
                }
            } finally {
                if (activeController === controller) {
                    activeController = null;
                    window.abortCurrentListRequest = undefined;
                    window.hideSpinner?.();
                }
            }
        },
        async viewSavedList() {
            const listEl = document.getElementById('list');
            if (!listEl || !this.savedListRoutes?.view) {
                return;
            }

            document.getElementById('tooltip_listLiveView')?.style.setProperty('display', 'none');
            window.showSpinner?.();

            const controller = new AbortController();
            activeController = controller;
            window.abortCurrentListRequest = () => controller.abort();

            try {
                const response = await fetch(this.savedListRoutes.view, {
                    method: 'GET',
                    headers: {
                        Accept: 'text/html',
                    },
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const html = await response.text();
                listEl.innerHTML = html;
                window.showListActions?.();
            } catch (e) {
                if (e?.name !== 'AbortError') {
                    this.$notify.error('Fehler beim Laden der Liste');
                }
            } finally {
                if (activeController === controller) {
                    activeController = null;
                    window.abortCurrentListRequest = undefined;
                    window.hideSpinner?.();
                }
            }
        },
        downloadPdf() {
            if (!this.isInEditMode() && !this.hasUnsavedChanges() && this.savedListRoutes?.pdf) {
                window.open(this.savedListRoutes.pdf, '_blank');
                return;
            }
            this.submitFormToUrl(this.liveRoutes.pdf);
        },
        downloadExcel() {
            if (!this.isInEditMode() && !this.hasUnsavedChanges() && this.savedListRoutes?.excel) {
                window.open(this.savedListRoutes.excel, '_blank');
                return;
            }
            this.submitFormToUrl(this.liveRoutes.excel);
        },
        isInEditMode() {
            return document.getElementById('listEditMode') !== null;
        },
        hasUnsavedChanges() {
            if (!this.listId) {
                return true;
            }
            const currentHash = this.stringToHashCode(this.serializeListConfiguration() || '');
            return this.originalConfigurationHash !== currentHash;
        },
        submitFormToUrl(url) {
            const form = this.getElement_listConfiguration();
            if (!form || !url) {
                return;
            }

            const submitForm = document.createElement('form');
            submitForm.method = 'post';
            submitForm.action = url;

            new FormData(form).forEach((value, key) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value;
                submitForm.appendChild(input);
            });

            document.body.appendChild(submitForm);
            submitForm.submit();
            submitForm.remove();
        },

        enqueue() {
            const newConfigurationHash = this.stringToHashCode(this.serializeListConfiguration());
            if (!this.listId || this.originalConfigurationHash !== newConfigurationHash) {
                showAlert(this.$t('Nicht gespeicherte Änderungen'), this.$t('Bitte speichern Sie zunächst die Liste, um sie im Hintergrund berechnen zu können.'));
                return;
            }

            if (this.getListName().trim() === '') {
                showAlert(this.$t('Name fehlt'), this.$t('Bitte geben Sie der Liste zunächst einen Namen, um sie im Hintergrund berechnen zu können.'));
                return;
            }

            this.enqueuing = true;
            this.enqueuingOverall = true;
            // Always send the selection, never a conditional null. formatSelectable only arrives with the
            // enqueue GET, and this button is clickable before that resolves — gating on it would send null on
            // a fast click and get all three formats built, PDF included, which is the waste this removes.
            // Sending it unconditionally is safe because ListExportFormatPolicy ignores the field for
            // kindergartens anyway, so formatSelectable decides only whether the checkbox is SHOWN.
            ListPrefetchedDataRepository.enqueue(this.listId, this.notifyUser, this.selectedFormats)
                .then(() => this.enqueuing = false)
                .catch(() => this.$notify.error('Liste konnte nicht für die Berechnung im Hintergrund vorgemerkt werden'));
        },
        stringToHashCode(string) {
            // @see https://stackoverflow.com/a/7616484
            let hash = 0; let i; let chr;
            if (string.length === 0) {
                return hash;
            }

            for (i = 0; i < string.length; i++) {
                chr = string.charCodeAt(i);
                hash = ((hash << 5) - hash) + chr;
                hash |= 0; // Convert to 32bit integer
            }

            return hash;
        },
        updateConfigurationHash() {
            const serialized = this.serializeListConfiguration();
            if (!serialized) {
                return false;
            }

            this.originalConfigurationHash = this.stringToHashCode(serialized);
        },
        serializeListConfiguration() {
            const form = this.getElement_listConfiguration();
            if (!form) {
                return null;
            }

            return new URLSearchParams(new FormData(form)).toString();
        },
        getListName() {
            const form = this.getElement_listConfiguration();
            if (!form) {
                return '';
            }

            const name = new FormData(form).get('name');
            return typeof name === 'string' ? name : '';
        },

        // External DOM element accessor (form is still external to this component)
        getElement_listConfiguration() {
            return document.getElementById('list_configuration');
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
// The stacked checkboxes carry no vertical margin of their own — .kgr-checkbox sets none, and the 0.5em only
// ever came from the --inline modifier these do not use.
.enqueue-options {
    display: flex;
    flex-direction: column;
    gap: var(--kgr-default-margin-small);
    margin-bottom: var(--kgr-default-margin-small);
}
</style>
